import {TicketStatusCategory} from '@frontapp/ticketing';
import * as t from 'io-ts';

import {RTagIdRef} from './apiTypes';
import {IotsType} from './commonApiTypes';
import {parseTicketStatus, RTicketStatus, RTicketStatusCategory} from './ticketStatusModel';
import {makeSafeArrayType} from './types/arrayTypes';
import {collectEnumValues} from './types/enumTypes';
import {makeOptional} from './types/optionalType';

/*
 * Runtime types.
 */

const RBaseTicketStatusCategoryPreferences = t.type({
  defaultTagId: RTagIdRef,
});

const ROpenTicketStatusCategoryPreferences = t.intersection([
  RBaseTicketStatusCategoryPreferences,
  t.type({
    reopenTagId: makeOptional(RTagIdRef),
  }),
]);

export const RTicketStatusCategoryPreferences = t.intersection([
  t.record(RTicketStatusCategory, RBaseTicketStatusCategoryPreferences),
  t.record(t.literal(TicketStatusCategory.OPEN), ROpenTicketStatusCategoryPreferences),
]);

export const RTicketingPreferences = t.type({
  statusCategories: RTicketStatusCategoryPreferences,
  statuses: makeSafeArrayType(RTicketStatus),
});

/*
 * Types.
 */

export interface TicketingPreferencesModel extends IotsType<typeof RTicketingPreferences> {}

/*
 * Serialization/deserialization.
 */

export function parseTicketStatusCategoryPreferences(
  src: any,
): TicketingPreferencesModel['statusCategories'] {
  return collectEnumValues(TicketStatusCategory).reduce(
    /* eslint no-param-reassign: 0 */
    (statusCategories, category) => {
      const defaultTagId = src.ticket_status_categories[category].default_tag_id;

      switch (category) {
        case TicketStatusCategory.OPEN: {
          statusCategories[category] = {
            defaultTagId,
            reopenTagId: src.ticket_status_categories[category].reopen_tag_id,
          };
          break;
        }

        default: {
          statusCategories[category] = {defaultTagId};
          break;
        }
      }

      return statusCategories;
    },
    {
      open: {defaultTagId: 0, reopenTagId: undefined},
      waiting: {defaultTagId: 0},
      resolved: {defaultTagId: 0},
    } satisfies TicketingPreferencesModel['statusCategories'],
  );
}

export function parseTicketingPreferences(src: any): TicketingPreferencesModel {
  return {
    statusCategories: parseTicketStatusCategoryPreferences(src),
    statuses: src.ticket_statuses.map(parseTicketStatus),
  };
}
